import { useEffect, useState } from "react";
import { api } from "../../../lib/api";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table";
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import type { Room, RoomState } from "ghost-server";
import { durationInputToSeconds, formatSecondsAsDuration, secondsToDurationInput } from "../../../lib/duration";

type Override = { roomId: string; state: string; transitionAfterSeconds: number };

type Props = {
	room: Room | null;
	onClose: () => void;
};

export function RoomStateOverridesDialog({ room, onClose }: Props) {
	const [states, setStates] = useState<RoomState[]>([]);
	const [overrides, setOverrides] = useState<Override[]>([]);
	const [editing, setEditing] = useState<string | null>(null);
	const [transitionAfter, setTransitionAfter] = useState("");

	const isOpen = !!room;

	async function load(roomId: string) {
		const [statesRes, overridesRes] = await Promise.all([
			api.admin.states.all.get(),
			api.admin.rooms["state-overrides"].get({ query: { roomId } }),
		]);
		if (statesRes.data) setStates(statesRes.data);
		if (overridesRes.data) setOverrides(overridesRes.data);
	}

	useEffect(() => {
		if (room) {
			setEditing(null);
			setTransitionAfter("");
			load(room.id);
		}
	}, [room?.id]);

	function overrideFor(state: string) {
		return overrides.find((o) => o.state === state);
	}

	function startEdit(state: string) {
		setEditing(state);
		setTransitionAfter(secondsToDurationInput(overrideFor(state)?.transitionAfterSeconds ?? 0));
	}

	async function saveEdit(state: string) {
		if (!room) return;
		const transitionAfterSeconds = durationInputToSeconds(transitionAfter);
		if (transitionAfterSeconds === undefined) return;

		await api.admin.rooms["state-overrides"].upsert.put({
			roomId: room.id,
			state,
			transitionAfterSeconds,
		});
		setEditing(null);
		await load(room.id);
	}

	async function clearOverride(state: string) {
		if (!room) return;
		await api.admin.rooms["state-overrides"].delete.delete({ roomId: room.id, state });
		await load(room.id);
	}

	return (
		<Dialog open={isOpen} onOpenChange={(o) => !o && onClose()}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>State Overrides{room && ` — ${room.name}`}</DialogTitle>
				</DialogHeader>
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>State</TableHead>
							<TableHead>Global</TableHead>
							<TableHead>Override</TableHead>
							<TableHead className="w-36" />
						</TableRow>
					</TableHeader>
					<TableBody>
						{states.map((s) => {
							const override = overrideFor(s.state);
							const isEditing = editing === s.state;
							return (
								<TableRow key={s.state}>
									<TableCell className="text-[#00ffcc]">{s.state}</TableCell>
									<TableCell>{formatSecondsAsDuration(s.transitionAfterSeconds)}</TableCell>
									<TableCell>
										{isEditing ? (
											<div className="space-y-1">
												<Label className="text-[10px] text-[#444]">HH:MM:SS</Label>
												<Input
													type="text"
													value={transitionAfter}
													onChange={(e) => setTransitionAfter(e.target.value)}
													placeholder="00:00:00"
													className="h-7 w-24"
													onKeyDown={(e) => e.key === "Enter" && saveEdit(s.state)}
												/>
											</div>
										) : override ? (
											<span className="text-[#ffcc00]">
												{formatSecondsAsDuration(override.transitionAfterSeconds)}
											</span>
										) : (
											<span className="text-[#333]">—</span>
										)}
									</TableCell>
									<TableCell>
										{isEditing ? (
											<div className="flex gap-1">
												<Button size="sm" onClick={() => saveEdit(s.state)}>save</Button>
												<Button size="sm" variant="ghost" onClick={() => setEditing(null)}>cancel</Button>
											</div>
										) : (
											<div className="flex gap-1">
												<Button size="sm" variant="ghost" onClick={() => startEdit(s.state)}>
													{override ? "edit" : "set"}
												</Button>
												{override && (
													<Button size="sm" variant="ghost" onClick={() => clearOverride(s.state)}>
														clear
													</Button>
												)}
											</div>
										)}
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
				<DialogFooter>
					<DialogClose asChild>
						<Button variant="ghost" size="sm">close</Button>
					</DialogClose>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
